import { connect } from 'react-redux';
import React, { useEffect, useRef } from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
/** import mui components in the section above, Sort Lines Ascending */

import { IncludeExcludeLayout } from 'widgets-v6/layout';
import { TextField } from 'widgets-v6/standard-input';
import CheckboxMenuList from 'widgets/checkbox-menu-list';
import FormField from 'widgets-v5/form-field';
import Separator from 'widgets-v5/separator';
import Spacer from 'widgets-v5/spacer';
import PreFixSuffixInput from 'widgets-v6/prefix-suffix-input';
/** import widgets components in the section above, Sort Lines Ascending */

import { applyAdPresetPropertyDisplayNames } from 'services/resources/ad-preset';
import { DeviceOS } from 'forms/ad-form/modules/targeting';
import { editAdPresetForm } from 'pages/ad-preset/ad-preset-edit/actions';
import { getCarrierIspOptions } from 'common/constants/carrier-targeting-v2';
import { getEnvironmentSettings } from 'services/environment';
import { isAdPresetFieldAllowedByEnvironment } from 'states/resources/ad-presets/business-logic';
import { newAdPresetForm } from 'pages/ad-preset/ad-preset-new/actions';
import { SELF_SERVE_ROOT_ORG } from 'states/profile/business-rules';
import { TargetCarriersIsps } from 'forms/ad-form/modules/target-carriers-isps';
import { TargetDeviceLanguage } from 'forms/ad-form/modules/target-device-language';
import { TargetDeviceLanguageExclude } from 'forms/ad-form/modules/target-device-language-exclude';
import { TargetDoohPublishers } from 'forms/ad-form/modules/target-dooh-publishers';
import { TargetGenders } from 'forms/ad-form/modules/checkbox-groups';
import DoohPublishersActions from 'states/resources/doohPublishers/actions';
import Geoboxes from 'forms/ad-form/modules/geo-boxes';
import Geotargets from 'forms/ad-form/modules/geotargets';
import selector from './selector';
import TargetAge from 'forms/ad-form/modules/target-age';
/** import single application imports in the section above, Sort Lines Ascending */

const useStyles = makeStyles(() => ({
    grid_right: {
        paddingLeft: '10px',
    },
    paper_root: {
        maxHeight: '650px',
        overflow: 'auto',
    },
    loading_root: {
        width: '100%',
        height: '500px',
    },
    formField: {
        paddingBottom: '30px',
    },
    grid_foot: {
        paddingTop: '20px',
    },
    widget: {
        padding: 10,
    },
    closeIcon: {
        marginRight: 5,
    },
}));

function AdPresets({
    location,
    handleClose,
    dispatch,
    defaultValues,
    EN_1421_adPresetId,
    showErrors,
    errors,
    formData,
    profileToken,
    isLoading,
    organizationId,
    doohPublishers,
}) {
    const classes = useStyles();
    const environmentSettings = getEnvironmentSettings();
    const isPropertyAllowedByEnvironment = property => {
        const fields =
            property.fields && property.fields.length > 0 ? property.fields : [property.key];
        return fields.every(field =>
            isAdPresetFieldAllowedByEnvironment(field, { environmentSettings })
        );
    };

    useEffect(() => {
        const campaignId =
            location && location.query && location.query.campaign ? location.query.campaign : '';
        if (EN_1421_adPresetId === 'new') {
            dispatch(newAdPresetForm.open(campaignId));
        } else {
            dispatch(editAdPresetForm.open(EN_1421_adPresetId));
        }
    }, [1]);

    useEffect(() => {
        dispatch(DoohPublishersActions.getAll());
    }, []);

    const onFieldChange = fieldName => {
        return fieldValue => {
            if (fieldName === 'ad_preset_name') {
                fieldValue = fieldValue.target.value;
            }

            const newFormData = {
                [fieldName]: fieldValue,
            };
            if (EN_1421_adPresetId === 'new') {
                dispatch(newAdPresetForm.updateDraft(newFormData));
            } else {
                dispatch(editAdPresetForm.updateDraft(newFormData));
            }
        };
    };

    const cancelAdPreset = () => {
        if (EN_1421_adPresetId === 'new') {
            dispatch(newAdPresetForm.cancelDraft(location.query));
        } else {
            dispatch(editAdPresetForm.cancelDraft());
        }
        handleClose();
    };

    const submitAdPreset = async () => {
        if (EN_1421_adPresetId === 'new') {
            await dispatch(newAdPresetForm.submitDraft(formData, location.query));
        } else {
            await dispatch(editAdPresetForm.submitDraft(formData, EN_1421_adPresetId));
        }

        if (Object.keys(errors).length < 1) {
            handleClose();
        }
    };

    const onMenuCheckboxChange = (key, value) => {
        // Seed/clear every checklist field (include + exclude) so validateAdPresetDraft
        // sees both keys when a compound property is enabled — matches edit-init prop.fields.
        const property = (formData.ad_preset_properties || [])
            .flatMap(group => group.properties || [])
            .find(prop => prop.key === key);
        const fields = property?.fields?.length > 0 ? property.fields : [key];

        const newFormData = {};
        for (const field of fields) {
            // value is current checked state: false = enabling, true = disabling
            newFormData[field] = value ? undefined : defaultValues[field];
        }

        if (EN_1421_adPresetId === 'new') {
            dispatch(newAdPresetForm.updateDraft(newFormData));
        } else {
            dispatch(editAdPresetForm.updateDraft(newFormData));
        }
    };

    const widgets = [
        {
            key: 'geotargets',
            value: (
                <Geotargets
                    value={formData.geotargets}
                    errors={errors.geotargets}
                    showErrors={showErrors}
                    profileToken={profileToken}
                    onChange={onFieldChange('geotargets')}
                />
            ),
        },
        {
            key: 'geoboxes',
            value: (
                <Geoboxes
                    ref={useRef('geoboxes')} //"geoboxes"
                    value={formData.geoboxes}
                    errors={errors.geoboxes}
                    showErrors={showErrors}
                    onChange={onFieldChange('geoboxes')}
                />
            ),
        },
        {
            key: 'target_device_os',
            value: (
                <DeviceOS
                    formData={formData}
                    onFieldChange={onFieldChange('target_device_os')}
                    isCrossPlatformCampaign={true}
                    deviceOsWarnings={{}}
                />
            ),
        },
        {
            key: 'target_carriers',
            value: (
                <FormField
                    description=""
                    errors={errors.target_carriers || errors.carriers_exclude}
                    showErrors={showErrors}
                >
                    <TargetCarriersIsps
                        onFieldChange={onFieldChange}
                        options={getCarrierIspOptions({
                            isCrossPlatformCampaign: true,
                        })}
                        carrierISPIncludeValue={formData.target_carriers || []}
                        carrierISPExcludeValue={formData.carriers_exclude || []}
                        carrierIspWarning={{}}
                        isCrossPlatformCampaign={false}
                    />
                </FormField>
            ),
        },
        {
            key: 'target_device_language',
            value: (
                <FormField
                    description=""
                    errors={errors.target_device_language || errors.target_device_language_exclude}
                    showErrors={showErrors}
                >
                    <IncludeExcludeLayout type="Include" marginBottom>
                        {/* Errors stay on the outer FormField only — same as Carrier / DOOH. */}
                        <TargetDeviceLanguage
                            value={formData.target_device_language || []}
                            onChange={onFieldChange('target_device_language')}
                        />
                    </IncludeExcludeLayout>
                    <IncludeExcludeLayout type="Exclude">
                        <TargetDeviceLanguageExclude
                            value={formData.target_device_language_exclude || []}
                            onChange={onFieldChange('target_device_language_exclude')}
                        />
                    </IncludeExcludeLayout>
                </FormField>
            ),
        },
        {
            key: 'target_genders',
            value: (
                <TargetGenders
                    value={formData.target_genders}
                    errors={errors.target_genders}
                    showErrors={showErrors}
                    onChange={onFieldChange('target_genders')}
                />
            ),
        },
        {
            key: 'target_age_groups',
            value: (
                <TargetAge
                    value={formData.target_age_groups}
                    errors={errors.targetAgeGroup}
                    showErrors={showErrors}
                    onChange={onFieldChange('target_age_groups')}
                />
            ),
        },
        {
            key: 'max_cpm_rate_local',
            value: (
                <FormField
                    description="Some fees may be deducted from your Total Bid Price. Total Bid Price only applies when the ad uses Fixed bid price. It is not available for automatic bid."
                    errors={errors.max_cpm_rate_local}
                    showErrors={showErrors}
                >
                    <PreFixSuffixInput
                        prefix={environmentSettings.getCurrencySign()}
                        suffix="CPM"
                        formatIn={formatSpendValueToString}
                        formatOut={formatSpendStringToValue}
                        shouldAllowInput={shouldAllowInputForSpend}
                        onChange={onFieldChange('max_cpm_rate_local')}
                        value={formData.max_cpm_rate_local}
                    />
                </FormField>
            ),
        },
        {
            key: 'target_dooh_publishers',
            value: (
                <FormField
                    description=""
                    errors={errors.target_dooh_publishers}
                    showErrors={showErrors}
                >
                    <TargetDoohPublishers
                        onFieldChange={onFieldChange}
                        doohPublishers={doohPublishers}
                        target_dooh_publishers={formData.target_dooh_publishers}
                        target_dooh_publishers_exclude={formData.target_dooh_publishers_exclude}
                    />
                </FormField>
            ),
        },
    ].filter(widget => isPropertyAllowedByEnvironment({ key: widget.key }));

    // Hide env-unsupported properties (Spain-like capabilities, DOOH, etc.) from checklist + widgets.
    // Overlay canonical ad-form names by key so stale Mongo names (e.g. Total Cost) never surface.
    const adPresetPropertiesForMenu = applyAdPresetPropertyDisplayNames(
        (formData.ad_preset_properties || [])
            .map(group => ({
                ...group,
                properties: (group.properties || []).filter(isPropertyAllowedByEnvironment),
            }))
            .filter(group => group.properties.length > 0)
    );

    const isAnyWidgetSelected = adPresetPropertiesForMenu.some(group =>
        (group.properties || []).some(property => property.checked)
    );

    const widgetsByKey = Object.fromEntries(widgets.map(widget => [widget.key, widget.value]));

    if (isLoading) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                classes={{ root: classes.loading_root }}
            >
                <Grid item>
                    <CircularProgress isloading="true" />
                </Grid>
            </Grid>
        );
    }

    return (
        <React.Fragment>
            <Grid container justifyContent="space-between" alignItems="center">
                <DialogTitle>Ad Presets</DialogTitle>
                <div className={classes.closeIcon}>
                    <IconButton onClick={cancelAdPreset} size="large">
                        <CloseIcon />
                    </IconButton>
                </div>
            </Grid>
            <DialogContent dividers>
                <Grid container>
                    <Grid item xs={3}>
                        {/* Same FormLabel style/top as right-side section headers (e.g. Targeting). */}
                        <FormLabel style={{ fontWeight: 1000, lineHeight: 2 }} required>
                            Name
                        </FormLabel>
                        <TextField
                            onChange={onFieldChange('ad_preset_name')}
                            value={formData.ad_preset_name}
                            error={showErrors && errors.ad_preset_name}
                            fullWidth
                            required
                            disableMargin
                        />
                        {/* Show checkbox to set default preset only for non Self-Serve Root orgs */}
                        {organizationId !== SELF_SERVE_ROOT_ORG && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.is_default_preset}
                                        onChange={event =>
                                            onFieldChange('is_default_preset')(event.target.checked)
                                        }
                                    />
                                }
                                label="Auto apply preset to new ads"
                            />
                        )}
                        <CheckboxMenuList
                            values={adPresetPropertiesForMenu}
                            onMenuCheckboxChange={onMenuCheckboxChange}
                            onChange={onFieldChange('ad_preset_properties')}
                        />
                    </Grid>
                    <Grid item xs={9} classes={{ root: classes.grid_right }}>
                        <div className={classes.paper_root}>
                            {isAnyWidgetSelected ? (
                                adPresetPropertiesForMenu.flatMap(group => {
                                    const visibleProperties = (group.properties || []).filter(
                                        property => property.checked && widgetsByKey[property.key]
                                    );
                                    if (visibleProperties.length === 0) {
                                        return [];
                                    }

                                    return [
                                        <FormLabel
                                            key={`section-${group.groupName}`}
                                            style={{ fontWeight: 1000, lineHeight: 2 }}
                                        >
                                            {group.groupName}
                                        </FormLabel>,
                                        ...visibleProperties.map(property => (
                                            <div key={property.key} className={classes.widget}>
                                                {property.key !== 'geoboxes' ? (
                                                    <React.Fragment>
                                                        <Separator label={property.name} />
                                                        <Spacer />
                                                        {widgetsByKey[property.key]}
                                                    </React.Fragment>
                                                ) : (
                                                    widgetsByKey[property.key]
                                                )}
                                            </div>
                                        )),
                                    ];
                                })
                            ) : (
                                <FormField
                                    errors={errors.ad_preset_properties}
                                    showErrors={showErrors}
                                >
                                    <div>Select a preset option to begin.</div>
                                </FormField>
                            )}
                        </div>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={submitAdPreset} color="primary" variant="contained">
                    Save
                </Button>
            </DialogActions>
        </React.Fragment>
    );

    function shouldAllowInputForSpend(input) {
        // Allow empty input
        if (!input) {
            return true;
        }
        // Check allowable characters
        if (!/^[\d,]*(\.\d{0,2})?$/g.test(input)) {
            return false;
        }
        // Check character length
        const [dollars, cents] = input.split('.');
        const numDollarsDigits = dollars.match(/\d/g).length;
        const numCentsDigits = cents ? cents.length : 0;
        return numDollarsDigits <= 9 && numCentsDigits <= 2;
    }

    function formatSpendValueToString(value) {
        if (!value) {
            return '';
        } else {
            const [dollars, cents] = value.toFixed(2).split('.');
            const formattedDollars = dollars.replace(/(\d)(?=(\d{3})+\b)/g, '$1,');
            return `${formattedDollars}.${cents}`;
        }
    }

    function formatSpendStringToValue(string) {
        if (string === '') {
            return 0;
        } else {
            return parseFloat(string.replace(/,/g, ''));
        }
    }
}

export default connect(selector)(AdPresets);
